import React, { useState } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { Visibility, VisibilityOff, Business, PowerSettingsNew } from '@mui/icons-material';
import Logo from '../assets/logo.png'; // Tu imagen personalizada
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

export const LoginPage: React.FC = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    identifier: '',
    password: '',
  });
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      // Intentar login automáticamente detectando el tipo de usuario
      // Primero intenta como super admin, luego como empresa y finalmente como trabajador
      try {
        await login(formData.identifier, formData.password, 'super_admin');
        navigate('/workers');
        return;
      } catch (superAdminError) {
        // Si falla super admin, intentar como empresa
        try {
          await login(formData.identifier, formData.password, 'company');
          navigate('/workers');
          return;
        } catch (companyError) {
          // Intentar como trabajador
          try {
            await login(formData.identifier, formData.password, 'worker');
            navigate('/me');
            return;
          } catch (workerError) {
            throw new Error('Credenciales inválidas');
          }
        }
      }
    } catch (err: any) {
      setError(err.message || 'Error al iniciar sesión');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: '#4a90e2',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 2,
      }}
    >
      {/* Logo en el fondo azul */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <img src={Logo} alt="Check On" style={{ width: 80, height: 80, marginBottom: 8 }} />
        <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', color: '#ffffff' }}>
          Check On
        </Typography>
      </Box>

      <Paper
        elevation={8}
        sx={{
          padding: 3,
          width: '100%',
          maxWidth: 360,
          borderRadius: 2,
        }}
      >

        {/* Título del formulario */}
        <Typography variant="h6" component="h2" align="center" sx={{ mb: 3, color: 'text.secondary' }}>
          Iniciar Sesión
        </Typography>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Login Form */}
        <form onSubmit={handleSubmit}>
          {/* Formulario único para empresas y super admin */}
          <TextField
            label="ID"
            value={formData.identifier}
            onChange={(e) => setFormData({ ...formData, identifier: e.target.value })}
            fullWidth
            margin="normal"
            required
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Business />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            label="PIN"
            type={showPassword ? 'text' : 'password'}
            value={formData.password}
            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            fullWidth
            margin="normal"
            required
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Visibility />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Button
            type="submit"
            variant="contained"
            fullWidth
            size="large"
            disabled={loading}
            sx={{
              mt: 3,
              mb: 2,
              backgroundColor: '#4a90e2',
              '&:hover': {
                backgroundColor: '#357abd',
              },
            }}
          >
            {loading ? 'Iniciando sesión...' : 'Iniciar Sesión'}
          </Button>
        </form>
      </Paper>
    </Box>
  );
};
